/**
 * KnowledgeBasePage.tsx — Knowledge Base (Conoscenza) ingest surface.
 * Per-tenant company docs that feed the AI Chat (KB mode). Documenti table +
 * domain filter + upload modal + the read-only history of review decisions.
 * The pending Review Queue and its decision flow were removed 2026-09-16 with
 * light-only preparation (`D-59`): nothing produces new reviews any more. The
 * DECIDED records stay visible, because the same decision requires them kept.
 * Superseded header line follows for the record: AI-validator review queue + decision
 * history. Reactive via kbStore. Ported from prototype stage-kb-ingest.jsx.
 * KB domain → wiki-llm-service (B1a, live); frontend wiring still mock.
 */
import { useAuth0 } from "@auth0/auth0-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminOnlyDeleteDialog } from "../../components/ui/AdminOnlyDeleteDialog";
import { I } from "../../components/icons";
import { ApiError, apiErrorMessage } from "../../lib/api";
import { config } from "../../lib/config";
import {
  useDeleteKbDocument,
  useKbDeletionPreview,
  useRecheckKbDocument,
  recheckErrorIt,
  useIsKbAdmin,
  useKbDocuments,
  useKbReviews,
  useUploadKbDocument,
} from "../../lib/kbApi";
import { kbStore } from "../../lib/kbStore";
import { MOCK_KB_DOMAINS, type KbDoc, type KbDocStatus, type KbDomain } from "../../lib/mock";
import { KBDeleteDocumentModal } from "./KBDeleteDocumentModal";
import { SynthesisProgress } from "./SynthesisProgress";
import {
  DECISION_PILL,
  DomainChip,
  DomainDot,
  DomainPill,
  KBPlaceholder,
  KBStatusPill,
  ProjectRefLabel,
  ScanIncompleteNotice,
} from "./kbUi";

/** `D-59` — Omar, 2026-09-16: light-only preparation. «Concetti» and «Coda
 * revisione» are removed from the product, because nothing produces their
 * content any more: no concepts, no claims and no systematic contradiction
 * scans are generated. «Storico» stays — it is the read-only view of review
 * records the same decision requires to be PRESERVED, and it can only shrink.
 *
 * Removing the tab buttons is the removal, not a cosmetic hide: these are local
 * state, not routes, so there is no address left to reach the bodies by. The
 * server refuses the work itself (`ingestion_policy.enrichment_refused`), which
 * is the half that matters — a hidden button is not a stop. */
type Tab = "documenti" | "storico";

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        border: "none",
        background: "transparent",
        padding: "12px 16px",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? "var(--c-ink)" : "var(--c-ink-3)",
        borderBottom: active ? "2px solid var(--c-brand)" : "2px solid transparent",
        marginBottom: -1,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {children}
    </button>
  );
}

function fmtBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${bytes} B`;
}

// ─── Upload modal ────────────────────────────────────────────────────────────
function UploadModal({
  domains,
  onClose,
  onUpload,
  uploading,
}: {
  domains: KbDomain[];
  onClose: () => void;
  onUpload: (file: File, domain: string) => void;
  uploading: boolean;
}): JSX.Element {
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [domain, setDomain] = useState("");
  const [domainOpen, setDomainOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const picked = domains.find((d) => d.key === domain);
  const ready = Boolean(file && domain) && !uploading;
  const openPicker = (): void => inputRef.current?.click();

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "var(--c-bg-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        className="fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxWidth: "100%",
          background: "var(--c-bg-elev)",
          border: "1px solid var(--c-line)",
          borderRadius: 14,
          boxShadow: "var(--sh-pop)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 18px",
            borderBottom: "1px solid var(--c-line)",
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: "var(--c-brand-soft)",
              color: "var(--c-brand)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <I.Upload size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Carica nella Knowledge Base</div>
            <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
              Archivio aziendale per-tenant · indicizzato per l&apos;AI Chat
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <I.X size={14} />
          </button>
        </div>

        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <input
            ref={inputRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => {
              const picked = e.target.files?.[0];
              if (picked) setFile(picked);
              e.target.value = "";
            }}
          />
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) setFile(dropped);
            }}
            style={{
              border: `1.5px dashed ${drag ? "var(--c-brand)" : "var(--c-line-strong)"}`,
              background: drag ? "var(--c-brand-soft)" : "var(--c-bg)",
              borderRadius: 12,
              padding: "28px 20px",
              textAlign: "center",
              transition: "all var(--tr-fast)",
            }}
          >
            {file ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "center" }}
              >
                <I.PDF size={22} style={{ color: "#B53D2A" }} />
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{file.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--c-ink-3)" }}>
                    {fmtBytes(file.size)} · pronto
                  </div>
                </div>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setFile(null)}>
                  <I.X size={13} />
                </button>
              </div>
            ) : (
              <>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 9,
                    background: "var(--c-brand-soft)",
                    color: "var(--c-brand)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 10,
                  }}
                >
                  <I.Upload size={18} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 3 }}>
                  Trascina un documento qui
                </div>
                <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginBottom: 12 }}>
                  PDF · Excel · Markdown — fino a 50 MB
                </div>
                <button className="btn btn-sm" onClick={openPicker}>
                  Sfoglia file
                </button>
              </>
            )}
          </div>

          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "var(--c-ink-2)",
                display: "block",
                marginBottom: 6,
              }}
            >
              Dominio <span style={{ color: "var(--c-err)" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <button
                className="btn"
                onClick={() => setDomainOpen((o) => !o)}
                style={{ width: "100%", justifyContent: "space-between", height: 34 }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    color: picked ? "var(--c-ink)" : "var(--c-ink-3)",
                  }}
                >
                  {picked && <DomainDot color={picked.color} />}
                  {picked ? picked.label : "Seleziona un dominio…"}
                </span>
                <I.ChevronD size={13} style={{ color: "var(--c-ink-3)" }} />
              </button>
              {domainOpen && (
                <>
                  <div
                    onClick={() => setDomainOpen(false)}
                    style={{ position: "fixed", inset: 0, zIndex: 1 }}
                  />
                  <div
                    className="fade-in"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 4px)",
                      left: 0,
                      right: 0,
                      zIndex: 2,
                      background: "var(--c-bg-elev)",
                      border: "1px solid var(--c-line)",
                      borderRadius: 10,
                      boxShadow: "var(--sh-pop)",
                      padding: 6,
                    }}
                  >
                    {domains.map((d) => (
                      <button
                        key={d.key}
                        onClick={() => {
                          setDomain(d.key);
                          setDomainOpen(false);
                        }}
                        style={{
                          appearance: "none",
                          border: "none",
                          cursor: "pointer",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 9,
                          textAlign: "left",
                          padding: "8px 10px",
                          borderRadius: 7,
                          background: "transparent",
                          fontFamily: "inherit",
                          fontSize: 12.5,
                          fontWeight: 500,
                          color: "var(--c-ink)",
                        }}
                      >
                        <DomainDot color={d.color} />
                        <span style={{ flex: 1 }}>{d.label}</span>
                        {domain === d.key && (
                          <I.Check size={13} style={{ color: "var(--c-brand)" }} />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 18px",
            borderTop: "1px solid var(--c-line)",
            background: "var(--c-bg)",
          }}
        >
          <span
            style={{
              fontSize: 11.5,
              color: "var(--c-ink-3)",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <I.Sparkle size={11} style={{ color: "var(--c-ai)" }} /> Il documento diventa
            disponibile al termine dell&apos;elaborazione; il validatore AI segnala eventuali
            contraddizioni nella coda di revisione senza bloccarlo.
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-sm" onClick={onClose}>
              Annulla
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => file && onUpload(file, domain)}
              disabled={!ready}
              style={{ opacity: ready ? 1 : 0.5 }}
            >
              <I.Upload size={12} /> {uploading ? "Caricamento…" : "Carica"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Toast ─────────────────────────────────────────────────────────────────
/**
 * `KBR-24` — «Controlla di nuovo», with its explanation reachable.
 *
 * The first version put the explanation in a `title` on a `<span>` wrapping a
 * `disabled` button. An independent review showed that reaches nobody who is not
 * using a mouse: a `disabled` button is out of the tab order in every browser, a
 * bare `<span>` is not focusable either, a native `title` renders on focus only
 * for the element that owns it, and a touch device has no hover at all. So the
 * one state the control exists to explain was the one state its explanation
 * could not be read in.
 *
 * What is done instead:
 *  - the button stays FOCUSABLE and uses `aria-disabled` rather than `disabled`,
 *    so a keyboard user can reach it and hear that it is unavailable;
 *  - the reason is a real element with an id, wired by `aria-describedby`, so a
 *    screen reader announces it as the button's description;
 *  - the reason is also rendered as VISIBLE text beside the control, so it needs
 *    neither a pointer nor assistive technology;
 *  - `onClick` returns early when unavailable, because `aria-disabled` is an
 *    announcement, not a guard — a rule this project has recorded twice.
 */
function RecheckButton({
  doc,
  busy,
  onClick,
}: {
  doc: KbDoc;
  busy: boolean;
  onClick: () => void;
}): JSX.Element {
  const available = doc.canRecheck === true && !busy;
  const reasonId = `recheck-reason-${doc.id}`;
  const reason = busy
    ? "Controllo in corso…"
    : doc.canRecheck
      ? "Ripeti solo il controllo delle contraddizioni su questo documento."
      : (doc.recheckBlockedReason ?? "Controllo non disponibile per questo documento.");
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <button
        className="btn btn-ghost btn-icon btn-sm"
        aria-label="Controlla di nuovo"
        aria-disabled={!available}
        aria-describedby={reasonId}
        style={available ? undefined : { opacity: 0.45, cursor: "not-allowed" }}
        onClick={(e) => {
          e.stopPropagation();
          // `aria-disabled` tells assistive technology the control is
          // unavailable; it does NOT stop a click. The guard belongs here.
          if (!available) return;
          onClick();
        }}
      >
        <I.Refresh size={13} />
      </button>
      <span
        id={reasonId}
        style={{
          fontSize: 11,
          color: "var(--c-ink-3)",
          maxWidth: 220,
          lineHeight: 1.3,
        }}
      >
        {reason}
      </span>
    </span>
  );
}

/**
 * The status pill, with the reason a document is not live rendered as text.
 *
 * `D-37` requires a document that could not finish validation to be left
 * *"unavailable and recoverable, with a clear explanation of its state"*. A pill
 * reading «Rifiutato» is a state without an explanation, and a hover tooltip on a
 * non-focusable pill is an explanation only a mouse user can obtain.
 */
function StatusWithReason({
  status,
  reason,
}: {
  status: KbDocStatus;
  reason?: string | null;
}): JSX.Element {
  // A `live` document never shows a reason, whatever the payload contains. The
  // server does not send one today — but that is the SERVER's guarantee, and a
  // screen that renders «Live» beside a failure message because it trusted a
  // contract has no defence of its own. An independent tester demonstrated it by
  // simply putting a stray value in the fixture.
  const show = status !== "live" ? reason : null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
      <KBStatusPill status={status} />
      {show ? (
        <span
          style={{
            fontSize: 11,
            color: "var(--c-ink-3)",
            maxWidth: 260,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={show}
        >
          {show}
        </span>
      ) : null}
    </span>
  );
}

function Toast({ message, onClose }: { message: string; onClose: () => void }): JSX.Element {
  return (
    <div
      className="fade-in"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 24,
        transform: "translateX(-50%)",
        zIndex: 80,
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "11px 16px",
        borderRadius: 10,
        background: "#16191F",
        color: "#F2F1EE",
        boxShadow: "var(--sh-pop)",
        maxWidth: 560,
      }}
    >
      <I.CheckC size={15} style={{ color: "var(--c-ok)", flexShrink: 0 }} />
      <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          appearance: "none",
          border: "none",
          background: "transparent",
          color: "#9CA0AB",
          cursor: "pointer",
          padding: 0,
          marginLeft: 4,
          display: "flex",
        }}
      >
        <I.X size={13} />
      </button>
    </div>
  );
}

/** Error/consent state for the admin-only review tabs (shares the KB audience). */
function ReviewErrorState({
  error,
  consenting,
  onConsent,
}: {
  error: unknown;
  consenting: boolean;
  onConsent: () => void;
}): JSX.Element {
  const tokenError = error instanceof ApiError && error.status === 401;
  return (
    <div
      style={{
        padding: "40px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        textAlign: "center",
      }}
    >
      <I.Alert size={22} style={{ color: "var(--c-warn)" }} />
      <div style={{ fontSize: 13, color: "var(--c-ink-2)", maxWidth: 380 }}>
        {tokenError
          ? "Accesso alla Knowledge Base non ancora concesso."
          : `Impossibile caricare le revisioni: ${apiErrorMessage(error)}`}
      </div>
      {tokenError && (
        <button className="btn btn-primary btn-sm" onClick={onConsent} disabled={consenting}>
          {consenting ? "Apertura…" : "Concedi l'accesso alla Knowledge Base"}
        </button>
      )}
    </div>
  );
}

export function KnowledgeBasePage(): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("documenti");
  const [domainFilter, setDomainFilter] = useState("Tutti");
  const [query, setQuery] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Documenti reads LIVE from wiki-llm-service (B1a). Review queue + history are
  // admin-only (RLS + require_admin); gate the calls so non-admins don't 403-loop.
  const isAdmin = useIsKbAdmin();
  const docsQuery = useKbDocuments();
  const allDocs = docsQuery.data ?? [];
  const uploadDoc = useUploadKbDocument();
  const deleteDoc = useDeleteKbDocument();
  // `D-40`. The filename is carried alongside the id because the dialog's title
  // and its success toast must name the file even if the documents list has
  // refetched underneath — reading it back out of the list at confirm time is
  // how a toast comes to say "undefined eliminato".
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; filename: string } | null>(null);
  const deletePreview = useKbDeletionPreview(deleteTarget?.id ?? null);
  // `D-41`. A SEPARATE piece of state from `deleteTarget`, deliberately: setting
  // `deleteTarget` is what fires `useKbDeletionPreview`, and a member must not
  // cause that request at all. Refusing "inside" the confirmation dialog would
  // already have fetched the list Omar said they must not receive.
  const [refusedDelete, setRefusedDelete] = useState<string | null>(null);
  const recheckDoc = useRecheckKbDocument();
  // Which rows have a re-check in flight, tracked HERE rather than read off the
  // mutation. One `useMutation` instance is shared by every row, and an
  // independent tester traced what that costs in the installed query-core: a
  // second `.mutate()` detaches the observer from the first mutation before it
  // settles, so the first row's `isPending` goes false while its request is
  // still open AND its own success/error callback never fires. Measured: click
  // row 1, click row 2, and row 1 reads available again, shows no toast, and a
  // third click issues a genuine SECOND live request for row 1.
  //
  // `recheckDoc.variables === d.id` was my fix for the same symptom and is
  // wrong for the same reason — it tracks the LATEST call, not each row's own.
  // A set of ids owned by this component depends on nothing the library does
  // with observers.
  const [recheckBusy, setRecheckBusy] = useState<ReadonlySet<string>>(new Set());
  const decidedQuery = useKbReviews({ decided: true, enabled: isAdmin });
  const decided = decidedQuery.data ?? [];

  // On localhost Auth0 forces a consent screen the silent token flow can't show
  // (consent_required), so the first KB token request fails. A one-time popup
  // consent records the grant; silent loads work afterward. Shown only on a 401
  // token error. (See Auth0 notes / B1b_backend_build_plan.)
  const { getAccessTokenWithPopup, user } = useAuth0();
  const [consenting, setConsenting] = useState(false);
  const tokenError = docsQuery.error instanceof ApiError && docsQuery.error.status === 401;
  async function grantKbAccess(): Promise<void> {
    setConsenting(true);
    try {
      // Pin the CURRENT session's Organization. The popup runs a full
      // authorization_code flow, and unlike the silent iframe it does not consult
      // the org hint — so with no `organization` the SDK skips its org-match check
      // (`_verifyIdToken` only compares when one is supplied) and would accept a
      // token minted for a DIFFERENT tenant. ACC-3 stays intact: nothing is pinned
      // at *login*, only here at second-audience consent, where the answer is
      // already known from the token we hold.
      await getAccessTokenWithPopup({
        authorizationParams: {
          audience: config.wikiLLMAudience,
          ...(user?.org_id ? { organization: user.org_id as string } : {}),
        },
      });
      await docsQuery.refetch();
      if (isAdmin) await decidedQuery.refetch();
    } catch {
      // popup blocked or dismissed — the error message stays visible
    } finally {
      setConsenting(false);
    }
  }

  function showToast(msg: string): void {
    setToast(msg);
  }

  function doUpload(file: File, domain: string): void {
    uploadDoc.mutate(
      { file, domain, title: file.name },
      {
        onSuccess: () => {
          setUploadOpen(false);
          showToast(`”${file.name}” caricato — in elaborazione…`);
        },
        onError: (err) => showToast(`Caricamento non riuscito: ${apiErrorMessage(err)}`),
      },
    );
  }

  /**
   * `D-40` — deletion is irreversible and takes colleagues' summaries with it,
   * so it goes through a dialog that shows what will be lost.
   *
   * `window.confirm` used to do this job with one line of text. It cannot do it
   * any more: the sentence Omar approved has to sit beside a LIST computed by
   * the server, and a browser confirm can render neither the list nor the
   * distinction between a summary that is machine-written and one a colleague
   * edited. This only opens the dialog; nothing is sent until «Elimina PDF e
   * sintesi» is pressed.
   */
  function onDeleteDoc(d: KbDoc): void {
    // `D-41`, Omar 2026-09-06 — administrator-only, and the refusal happens
    // BEFORE `setDeleteTarget`, which is the line that would fetch the preview.
    // The server refuses both routes too; this is the half that stops the
    // request being made, which is what *"they must not receive the
    // affected-summary list through that action"* actually requires.
    if (!isAdmin) {
      setRefusedDelete(d.filename);
      return;
    }
    setDeleteTarget({ id: d.id, filename: d.filename });
  }

  function onConfirmDelete(): void {
    const target = deleteTarget;
    if (!target) return;
    deleteDoc.mutate(target.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast(`"${target.filename}" eliminato, insieme alle sintesi collegate.`);
      },
      // The dialog STAYS OPEN on failure. Closing it would leave the person
      // looking at a list that still contains the document, with a toast as the
      // only evidence that anything happened — and no way back to the confirm
      // except finding the row again.
      onError: (err) => showToast(`Eliminazione non riuscita: ${apiErrorMessage(err)}`),
    });
  }

  /**
   * `KBR-24` / `D-36` — «Controlla di nuovo».
   *
   * No client-side eligibility test: `d.canRecheck` is the SERVER's answer, and
   * the server checks again inside the same transaction that claims the
   * document, so two clicks a second apart cannot both start a scan. A 409 here
   * is not a bug — it is the authority disagreeing with a list that is at most
   * 2,5 seconds old — and its `detail` is shown verbatim for the same reason the
   * disabled tooltip is: the rule lives in one place.
   */
  async function onRecheckDoc(d: KbDoc): Promise<void> {
    if (recheckBusy.has(d.id)) return;
    setRecheckBusy((prev) => new Set(prev).add(d.id));
    try {
      await recheckDoc.mutateAsync(d.id);
      showToast(
        `"${d.filename}" — controllo riavviato. Il documento resta non disponibile finché non finisce.`,
      );
    } catch (err) {
      // `recheckErrorIt`, not `apiErrorMessage`: the server's 409 carries a
      // machine-readable code so this sentence can be Italian. See its own note.
      showToast(`Controllo non riuscito: ${recheckErrorIt(err)}`);
    } finally {
      setRecheckBusy((prev) => {
        const next = new Set(prev);
        next.delete(d.id);
        return next;
      });
    }
  }

  const filtered = allDocs.filter((d) => {
    if (d.status === "archived") return false; // soft-deleted docs are hidden from the active view
    if (domainFilter !== "Tutti" && d.domain !== domainFilter) return false;
    if (query.trim() && !d.filename.toLowerCase().includes(query.trim().toLowerCase()))
      return false;
    return true;
  });

  return (
    <div style={{ padding: "20px 28px 60px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <div>
          <div className="t-eyebrow" style={{ marginBottom: 6 }}>
            Archivio · per-tenant
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0, letterSpacing: "-0.02em" }}>
            Knowledge Base
          </h1>
          <p style={{ fontSize: 13, color: "var(--c-ink-3)", marginTop: 4 }}>
            Conoscenza aziendale · archivio per-tenant che alimenta l&apos;AI Chat (modalità KB).
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setUploadOpen(true)}>
          <I.Upload size={13} /> Carica documenti
        </button>
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--c-line)" }}>
          <TabBtn active={tab === "documenti"} onClick={() => setTab("documenti")}>
            Documenti
          </TabBtn>
          <TabBtn active={tab === "storico"} onClick={() => setTab("storico")}>
            Storico
          </TabBtn>
        </div>

        {tab === "documenti" && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                borderBottom: "1px solid var(--c-line)",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <DomainChip
                  label="Tutti"
                  active={domainFilter === "Tutti"}
                  onClick={() => setDomainFilter("Tutti")}
                />
                {MOCK_KB_DOMAINS.map((d) => (
                  <DomainChip
                    key={d.key}
                    label={d.label}
                    color={d.color}
                    active={domainFilter === d.key}
                    onClick={() => setDomainFilter(d.key)}
                  />
                ))}
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ position: "relative" }}>
                <I.Search
                  size={13}
                  style={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--c-ink-3)",
                  }}
                />
                <input
                  className="input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cerca documenti…"
                  style={{ height: 28, fontSize: 12, paddingLeft: 26, width: 220 }}
                />
              </div>
            </div>

            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>ID</th>
                  <th>Documento</th>
                  <th style={{ width: 140 }}>Dominio</th>
                  <th style={{ width: 140 }}>Caricato da</th>
                  <th style={{ width: 110 }}>Data</th>
                  <th style={{ width: 80, textAlign: "right" }}>Chunk</th>
                  <th style={{ width: 130 }}>Stato</th>
                </tr>
              </thead>
              <tbody>
                {docsQuery.isPending && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", color: "var(--c-ink-3)", padding: "32px 0" }}
                    >
                      Caricamento documenti…
                    </td>
                  </tr>
                )}
                {docsQuery.isError && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "28px 16px" }}>
                      <div
                        style={{
                          color: "var(--c-err)",
                          fontSize: 12.5,
                          marginBottom: tokenError ? 12 : 0,
                        }}
                      >
                        {apiErrorMessage(docsQuery.error)}
                      </div>
                      {tokenError && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => void grantKbAccess()}
                          disabled={consenting}
                        >
                          {consenting ? "Apertura…" : "Concedi l'accesso alla Knowledge Base"}
                        </button>
                      )}
                    </td>
                  </tr>
                )}
                {!docsQuery.isPending &&
                  !docsQuery.isError &&
                  filtered.map((d: KbDoc) => {
                    const dom = kbStore.domain(d.domain);
                    const genPages = d.generatedPageIds ?? [];
                    return (
                      <tr key={d.id}>
                        <td className="t-mono" style={{ color: "var(--c-ink-3)", fontSize: 12 }}>
                          {d.id}
                        </td>
                        <td>
                          <div
                            style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
                          >
                            <DomainDot color={dom ? dom.color : "var(--c-ink-3)"} />
                            <span
                              style={{
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {d.filename}
                            </span>
                          </div>
                          {genPages.length > 0 && (
                            <button
                              onClick={() => navigate("/kb/explore")}
                              style={{
                                appearance: "none",
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                display: "inline-block",
                                padding: 0,
                                marginTop: 2,
                                marginLeft: 16,
                                fontSize: 11,
                                fontWeight: 500,
                                color: "var(--c-info)",
                              }}
                            >
                              → {genPages.length}{" "}
                              {genPages.length === 1 ? "pagina generata" : "pagine generate"}
                            </button>
                          )}
                        </td>
                        <td>
                          <DomainPill domainKey={d.domain} />
                          <ProjectRefLabel doc={d} />
                        </td>
                        <td style={{ color: "var(--c-ink-2)", fontSize: 12.5 }}>{d.uploader}</td>
                        <td style={{ color: "var(--c-ink-3)", fontSize: 12 }}>{d.date}</td>
                        <td
                          className="t-num t-mono"
                          style={{ textAlign: "right", color: "var(--c-ink-2)" }}
                        >
                          {d.chunks}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                            }}
                          >
                            {d.status === "ingesting" ? (
                              <SynthesisProgress variant="compact" stage={d.synthesisStage} />
                            ) : (
                              // Wrapped so the explanation has somewhere to live:
                              // a document that is not live now carries the
                              // reason its own run recorded, which `D-37`
                              // requires and which the pill alone cannot say.
                              <StatusWithReason status={d.status} reason={d.lastError} />
                            )}
                            {/* `KBR-62` — beside the status, never instead of
                                it. «live» and «controllo incompleto» are two
                                different facts about one document, and two of
                                the first real tender's documents were both. */}
                            <ScanIncompleteNotice scan={d.scan} compact />
                            {/* `D-53` / `KB-14` — a document whose synthesis
                                was refused for size: no synthesis pages. Said
                                on the row, because «Live» alone would imply a
                                synthesis it lacks.

                                HON-1 (adversarial review, 2026-09-13): this
                                block used to end «Il testo originale resta
                                ricercabile.» UNCONDITIONALLY, and that half is
                                only true of a LIVE document. The backend
                                derives `unavailable_too_large` from
                                `run_stats->>'synthesis_input_tokens' IS NOT
                                NULL` on the newest run with NO status
                                predicate (`documents.py:469`), and the
                                pre-`D-53` pipeline wrote that very key while
                                setting the document `rejected` — so until an
                                admin calls `POST
                                /maintenance/apply-availability-policy` (QA
                                holds at least one such row today), and again
                                after any future `keep_existing` rejection of a
                                size-marked document, the row printed pill
                                «Rifiutato», the reason «non è ricercabile
                                dall'assistente» and «resta ricercabile» side by
                                side. Retrieval requires `live`. The size
                                explanation is true whatever the status; only
                                the searchability claim is gated. */}
                            {d.synthesisState === "unavailable_too_large" && (
                              <span
                                data-testid="synthesis-too-large-note"
                                style={{ fontSize: 11, color: "var(--c-ink-3)" }}
                              >
                                Sintesi non disponibile — documento molto lungo (KB-14).
                                {d.status === "live" && " Il testo originale resta ricercabile."}
                              </span>
                            )}
                            {isAdmin && (
                              <RecheckButton
                                doc={d}
                                busy={recheckBusy.has(d.id)}
                                onClick={() => void onRecheckDoc(d)}
                              />
                            )}
                            <button
                              className="btn btn-ghost btn-icon btn-sm"
                              title="Elimina documento"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteDoc(d);
                              }}
                              disabled={deleteDoc.isPending}
                            >
                              <I.Trash size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                {!docsQuery.isPending && !docsQuery.isError && filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: "center", color: "var(--c-ink-3)", padding: "32px 0" }}
                    >
                      Nessun documento corrisponde ai filtri.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div
              style={{
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--c-ink-3)",
                fontSize: 12,
              }}
            >
              <I.Info size={12} /> {filtered.length} document{filtered.length === 1 ? "o" : "i"} ·
              archivio scoped al tenant attivo · nessun dato condiviso tra organizzazioni.
            </div>
          </>
        )}

        {tab === "storico" &&
          (!isAdmin ? (
            <KBPlaceholder
              icon={I.Alert}
              title="Riservato agli amministratori"
              body="Lo storico delle decisioni di revisione è visibile solo agli amministratori del workspace."
            />
          ) : decidedQuery.isPending ? (
            <KBPlaceholder
              icon={I.Sparkle}
              title="Caricamento…"
              body="Recupero dello storico delle decisioni."
            />
          ) : decidedQuery.isError ? (
            <ReviewErrorState
              error={decidedQuery.error}
              consenting={consenting}
              onConsent={() => void grantKbAccess()}
            />
          ) : decided.length === 0 ? (
            <KBPlaceholder
              icon={I.History}
              title="Nessuna decisione registrata"
              body="Le decisioni di revisione approvate dall'Admin compariranno qui."
            />
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>ID</th>
                  <th>Documento</th>
                  <th style={{ width: 130 }}>Dominio</th>
                  <th style={{ width: 170 }}>Decisione</th>
                  <th style={{ width: 150 }}>Decisor</th>
                  <th style={{ width: 150 }}>Data decisione</th>
                </tr>
              </thead>
              <tbody>
                {decided.map((r) => {
                  const dp = r.decision ? DECISION_PILL[r.decision] : DECISION_PILL.reject_new;
                  return (
                    <tr key={r.id}>
                      <td className="t-mono" style={{ color: "var(--c-ink-3)", fontSize: 12 }}>
                        {r.id}
                      </td>
                      <td style={{ fontWeight: 500, fontSize: 12.5 }}>{r.newDocSource}</td>
                      <td>
                        <DomainPill domainKey={r.domain} />
                      </td>
                      <td>
                        {dp.muted ? (
                          <span className="pill" style={{ opacity: 0.6 }}>
                            {dp.label}
                          </span>
                        ) : (
                          <span className={`pill ${dp.cls}`}>{dp.label}</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12.5, color: "var(--c-ink-2)" }}>{r.decidedBy}</td>
                      <td style={{ fontSize: 12, color: "var(--c-ink-3)" }}>{r.decidedAt}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ))}
      </div>

      {uploadOpen && (
        <UploadModal
          domains={MOCK_KB_DOMAINS}
          onClose={() => setUploadOpen(false)}
          onUpload={doUpload}
          uploading={uploadDoc.isPending}
        />
      )}
      {deleteTarget && (
        <KBDeleteDocumentModal
          filename={deleteTarget.filename}
          preview={deletePreview.data}
          isLoading={deletePreview.isPending}
          error={deletePreview.error}
          isDeleting={deleteDoc.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={onConfirmDelete}
        />
      )}
      {refusedDelete !== null && (
        <AdminOnlyDeleteDialog subject={refusedDelete} onDismiss={() => setRefusedDelete(null)} />
      )}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
